import { isString, isNil, isArray, isEmpty, get, size, has, forEach, isNumber } from 'lodash';
import moment from 'moment';
import 'moment/locale/de';

// needed to make localization working
import 'moment/locale/en-au';
import 'moment/locale/en-ca';
import 'moment/locale/en-gb';
import 'moment/locale/fr';
import 'moment/locale/nl';
import 'moment/locale/ru';

import {
  LOAD_MAX_LIMIT_SCHEDULE_TYPE,
  PGE_SCHEDULE_TYPE,
  POWER_MAX_LIMIT_SCHEDULE_TYPE,
} from '../../screens/PlazaView/components/constants';
import { SCHEDULE_MAX_SECONDS_COUNT } from '../../screens/PlazaView/components/Editor/NodeForm/FormField/ScheduleField';
import { getPhasesOptions, getValueFromSource, phaseKey } from '../../screens/PlazaView/components/Editor/NodeForm/helper';
import { checkFileExt } from '../resources/helper';

export const UNSUPPORTED_FILE_TYPE_MESSAGE = 'Unsupported file type';

export const connectedPhases = (value, conf) => {
  const phasesOptions = getPhasesOptions({ formState: conf.data, node: conf.data });
  const key = !isString(value) ? phaseKey(value.relativeRotation || {}) : value;
  let invalid = 'Required';

  phasesOptions.forEach((option) => {
    if (option.value === key) {
      invalid = undefined;
    }
  });

  return invalid;
};

export function required(value) {
  const isValueExists = isString(value) ? value.trim() : !isNil(value) && !(isArray(value) && isEmpty(value));

  return isValueExists ? undefined : 'Required';
}

export const customerIdValidator = (value) =>
  // eslint-disable-next-line no-useless-escape
  value && !/^[a-z0-9A-Z\-]+$/.test(value) ? 'Customer ID can contain only letters, numbers and dashes in it' : undefined;

export const customerNameValidator = (value) => {
  // eslint-disable-next-line no-useless-escape
  if (value && !/^[a-zA-Z0-9\s\.'-]+$/.test(value)) {
    return 'Customer Name can contain only letters, numbers, apostrophes, dashes, spaces and dots in it';
  }
};

export const fwUpdateNameValidator = (value) => {
  // eslint-disable-next-line no-useless-escape
  if (value && !/^[a-zA-Z0-9\.\-_!@*()=:;/<>]+$/.test(value)) {
    return "Valid Characters include A-z, 0-9, '.', '_', '-', and '!@*()=:;/<>'.";
  }
};

export const scheduleFiledValidator = (items) => {
  const totalSeconds = items.reduce((acc, item) => {
    const diff = item.to - item.from || 1;

    return acc + (diff > 0 ? diff : 1);
  }, 0);

  if (totalSeconds !== SCHEDULE_MAX_SECONDS_COUNT) {
    return 'Full schedule should cover 24 hours without any gaps. End Time should be later than Start Time.';
  }
};

const getFieldName = (object, property) => (has(object, property) ? property : null);

export const getScheduleMainField = (staticData) => getFieldName(staticData, 'maxPower') || getFieldName(staticData, 'maxCurrentLimit');

export const getScheduleMainFieldByType = (scheduleType) => {
  switch (scheduleType) {
    case POWER_MAX_LIMIT_SCHEDULE_TYPE:
      return 'maxPower';
    case LOAD_MAX_LIMIT_SCHEDULE_TYPE:
      return 'maxLoad';
    case PGE_SCHEDULE_TYPE:
      return 'maxPeakDemand';
    default:
      return null;
  }
};

export const scScheduleFieldValidator = (items) => {
  let isEmpty = 0;
  let seconds = 0;

  const isRootNode = items[0].type === PGE_SCHEDULE_TYPE;

  const fieldName = getScheduleMainFieldByType(items[0].type);

  if (!fieldName) {
    return;
  }

  forEach(items, (item) => {
    const fieldValue = get(item, fieldName, '');

    if (!fieldValue.toString().length) {
      isEmpty++;
    }

    if (isRootNode && !get(item, 'cost', '').toString().length) {
      isEmpty++;
    }

    seconds += item.to - item.from || 1;
  });

  if (seconds === SCHEDULE_MAX_SECONDS_COUNT && isEmpty && items.length > 1) {
    return 'Please fill in all required fields.';
  }
};

export const testRegex = (regex) => {
  try {
    const regexp = new RegExp(regex);

    return (value) => {
      if (value && !regexp.test(value)) {
        return 'Invalid value';
      }
    };
  } catch (e) {
    console.error(`${regex} is  not a valid regular expression. \n ${e}`);

    return () => undefined;
  }
};

export const httpFirst = (value) => {
  const trimmedValue = value ? value.toString().trim() : null;

  if (trimmedValue) {
    if (!trimmedValue.startsWith('http://') && !trimmedValue.startsWith('https://')) {
      return 'URL should start with "http(s)://"';
    }
  }
};

export const jsonFile = (value) => {
  if (value && value.file && value.file.name) {
    const trimmedValue = value.file.name.toString().trim();

    if (trimmedValue && checkFileExt(trimmedValue, 'json', false)) {
      return UNSUPPORTED_FILE_TYPE_MESSAGE;
    }
  }
};

export const notJsFile = (value) => {
  if (value && value.file && value.file.name) {
    const trimmedValue = value.file.name.toString().trim();

    if (trimmedValue && checkFileExt(trimmedValue, 'js', true)) {
      return UNSUPPORTED_FILE_TYPE_MESSAGE;
    }
  }

  return undefined;
};

export const countryNotEmpty = (value, allValues) => (get(allValues, 'c_identity.c_country') ? undefined : 'Please select Country first');

export const isCountryValid = (value, allValues) => {
  if (!size(allValues._google_api_country_names)) {
    return undefined;
  }

  return Object.values(allValues._google_api_country_names).includes(allValues.c_identity.c_country)
    ? undefined
    : 'Valid address is required';
};

export const isIdentityNotEmpty = (value, allValues) => {
  const isFieldValid =
    get(allValues, 'c_identity.c_address') &&
    get(allValues, 'c_identity.c_latitude') &&
    get(allValues, 'c_identity.c_longitude') &&
    !isNil(get(allValues, 'c_identity.c_utc'));

  return isFieldValid ? undefined : 'Valid address is required.';
};

export const isIdentityNotEmptyForPlaza = (value, allValues) => {
  const isFieldValid =
    get(allValues, 'c_identity.c_address') && get(allValues, 'c_identity.c_latitude') && get(allValues, 'c_identity.c_longitude');

  return isFieldValid ? undefined : 'Valid address is required.';
};

export const isPending = (value) => (value === 'pending' ? 'Please change Charger Condition to Active' : undefined);

export const maxLength = (max) => (value) => (value && value.length > max ? `Must be ${max} characters or less` : undefined);

export const minLength = (min) => (value) => (value && value.trim().length < min ? `Enter at least ${min} characters` : undefined);

export const maxLength15 = maxLength(15);

export const number = (value) => (value && isNaN(Number(value)) ? 'Must be a number' : undefined);

export const minValue = (min) => (value) => (value && value < min ? `Must be at least ${min}` : undefined);

export const minValueFromSource = (source, defaultValue, sourceName) => (value) => (data) => {
  if (!source || !data) {
    return undefined;
  }

  const currentValue = parseFloat(value);
  const sourceValue = parseFloat(getValueFromSource(data, source)) || defaultValue;

  if (sourceName) {
    return currentValue < sourceValue ? `Must be greater or equal to "${sourceName}"` : undefined;
  }

  return currentValue < sourceValue ? `Must be at least ${sourceValue}` : undefined;
};

export const maxValue = (max) => (value) => (value && value > max ? `Must be not more ${max}` : undefined);

export const minValue18 = minValue(18);

export const email = (value) => (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ? 'Invalid email address' : undefined);

export const isNotIotechaEmail = (value) => {
  const domain = value.split('@')[1];

  return domain && domain.toLowerCase() === 'iotecha.com' ? '“@iotecha.com" emails can\'t be used"' : undefined;
};

const locale = window.navigator.userLanguage || window.navigator.language;

moment.locale([locale, 'en']);

const currentLocaleData = moment.localeData();

export const LLLFormat = currentLocaleData.longDateFormat('LLL');

export const datePickerDate = (value) =>
  value && !moment(value, LLLFormat, true).isValid() && !isNumber(value) ? 'Incorrect Data Format' : undefined;

export const uniqCName = (id) => (value, allValues) => {
  const cnames = get(allValues, 'c_i2p2_configuration.c_port_names', []).map((name) => name && name.toLowerCase());
  const valueLowered = value && value.toLowerCase();

  return value && value !== '' && cnames.includes(valueLowered) && cnames.indexOf(valueLowered) !== id
    ? 'This value already exists. Please try different one.'
    : undefined;
};

export const getMomentByUtc = (isUtc) => (isUtc ? moment.utc : moment);

export const datePickerFormat = currentLocaleData.longDateFormat('L');
export const LLFormat = currentLocaleData.longDateFormat('LL');
export const iotOnDateFormat = 'MMMM D, YYYY h:mm:ss A';
export const durationFormat = 'HH:mm:ss';

export const isFloat = (value) => (value && value % 1 !== 0 ? 'Must not be float' : undefined);

export const valueAccuracy = (value) => {
  const validNumber = number(value);

  if (validNumber) {
    return validNumber;
  }
  const validMinValue = minValue(0)(value);
  const validMaxValue = maxValue(10)(value);

  if (validMinValue || validMaxValue) {
    return 'Accuracy should be between 0 and 10';
  }
  const validateFloat = isFloat(value);

  if (validateFloat) {
    return validateFloat;
  }
};

export const valueMultiplicator = (value) => {
  if (!value) {
    return 'Multiplicator is required!';
  }
  if (Number(value) < 0) {
    return 'Multiplicator should be positive value!';
  }

  const notValidNumber = number(value);

  if (notValidNumber) return notValidNumber;

  if (value.toString().trim().replace(/[.,]/, '').length > 15) {
    return 'Multiplicator should not be more then 15 characters';
  }
};

export const range =
  ({ min, max }) =>
  (value) => {
    if (_.isUndefined(min) || _.isUndefined(max)) {
      return undefined;
    }

    if (value < min || value > max) {
      return `Values available: ${min} - ${max}`;
    }

    return undefined;
  };
